import { TipoAditivo } from '@vabembarqueja/embarque-ja-shared-components';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useAuthenticationContext } from '../app/features/authentication/authenticationContext';
import { UserInfo } from '../app/features/authentication/authenticationState';
import TokenService from '../app/features/authentication/services/Token.service';
import { TipoUsuario, UsuarioConta, UsuarioPosPagoState } from '../models/Usuario';
import { UsuarioService } from '../services';

interface Context {
  loading: boolean;
  userInfo: UserInfo | null;
  user: UsuarioConta | null;
  posPagoState: UsuarioPosPagoState | null;
  orgaoPublico: boolean;
  statusAditivo: TipoAditivo | null;
  refreshUserInfo: () => Promise<void>;
}

const UserContext = createContext<Context | null>(null);

const UserProvider = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState(true);
  const [orgaoPublico, setOrgaoPublico] = useState(false);
  const [statusAditivo, setStatusAditivo] = useState<TipoAditivo | null>(null);
  const { token } = useAuthenticationContext();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [posPagoState, setPosPagoState] = useState<UsuarioPosPagoState | null>(null);
  const [user, setUser] = useState<UsuarioConta | null>(null);

  const loadUserData = useCallback(async () => {
    const user = await UsuarioService.buscarConta();
    const posPago = await UsuarioService.getTipoUsuarioPosPago();
    setUser(user);
    if ('cnpj' in user) {
      setPosPagoState(posPago);
      setOrgaoPublico(user.orgaoPublico);
      setStatusAditivo(user.statusAditivo);
    } else {
      setPosPagoState(null);
    }
  }, []);
  const refreshUserInfo = useCallback(async () => {
    setLoading(true);
    try {
      await loadUserData();
    } finally {
      setLoading(false);
    }
  }, [loadUserData]);

  useEffect(() => {
    if (!token) return;

    setLoading(true);

    const parseTokenPromise = TokenService.parseToken(token).then(setUserInfo);
    const loadUserPromise = loadUserData();

    Promise.all([parseTokenPromise, loadUserPromise]).finally(() => setLoading(false));
  }, [token, loadUserData]);

  return (
    <UserContext.Provider
      value={{
        loading,
        user,
        userInfo,
        posPagoState,
        refreshUserInfo,
        orgaoPublico,
        statusAditivo
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

function useUserContext() {
  const context = useContext(UserContext);
  if (context === null) throw new Error('UserContext must be rendered inside UserProvider!');

  const { loading, user, userInfo, posPagoState, refreshUserInfo, orgaoPublico, statusAditivo } =
    context;
  if (loading || !userInfo) return { ...context, loading: true };

  const userPF = userInfo.extension_EmbarquejaClaim === 'UsuarioPF' && TipoUsuario.PESSOA_FISICA;
  const userPJ = userInfo.extension_EmbarquejaClaim === 'UsuarioPJ' && TipoUsuario.PESSOA_JURIDICA;
  const userClaim = userPF || userPJ || '';

  return {
    loading,
    user,
    userInfo,
    userClaim,
    posPagoState,
    orgaoPublico,
    statusAditivo,
    refreshUserInfo
  };
}

export { UserProvider, useUserContext };
